import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Chip, Link, Paper, Pill, Stack, Typography} from 'views/components';
import {WorkOutline as JobIcon} from 'views/icons';
import {InvitationFamilyAnswers, translate} from 'views/module';

import {formatDateTime, translateOr} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/format';
import {humanizeKind} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/workflows/workflow_status';

const STRINGS = 'caregiver.LoomCaregiverDetail.Invitations';

const STATUS_COLOURS = {WAITING: 'info', COMPLETED: 'success', DECLINED: 'warning'};

const SectionTitle = (props) => {
  return (
    <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#1A1916'}}>
      {props.children}
    </Typography>
  );
};
SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

const DetailCell = (props) => {
  const {label, value} = props;

  return (
    <Stack
      sx={{
        gap: '6px',
        minWidth: 0,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 0 0 0.5px #E8E4DE',
        px: 2,
        py: 1.5,
      }}
    >
      <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#A8A49E'}}>
        {label}
      </Typography>
      <Typography sx={{fontWeight: 700, fontSize: '12px', lineHeight: '16px', color: '#000000', wordBreak: 'break-word', whiteSpace: 'pre-wrap'}}>
        {value || '-'}
      </Typography>
    </Stack>
  );
};
DetailCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const yesNo = (value) => {
  return value === null || value === undefined ? null : translate(`${STRINGS}.Answers.Boolean.${value}`);
};

const answer = (value) => {
  return value && translateOr(`${STRINGS}.Answers.Answer.${value}`, humanizeKind(value));
};

const responseDetails = (response) => [
  {
    key: 'startDate',
    label: translate(`${STRINGS}.Answers.StartDate`),
    value: answer(response.startDateAnswer),
  },
  {
    key: 'tasksCannotDo',
    label: translate(`${STRINGS}.Answers.TasksCannotDo`),
    value: response.tasksCannotDo && (
      response.tasksCannotDo.length ?
        response.tasksCannotDo.map(humanizeKind).join(', ') :
        translate(`${STRINGS}.Answers.None`)
    ),
  },
  {
    key: 'languages',
    label: translate(`${STRINGS}.Answers.Languages`),
    value: (response.languageLevels || [])
        .map((entry) => `${humanizeKind(entry.language)}: ${translateOr(`${STRINGS}.Answers.Level.${entry.level}`, humanizeKind(entry.level))}`)
        .join(', '),
  },
  {
    key: 'location',
    label: translate(`${STRINGS}.Answers.Location`),
    value: answer(response.locationWorkable),
  },
  {
    key: 'considerations',
    label: translate(`${STRINGS}.Answers.Considerations`),
    value: yesNo(response.considerationsConfirmed),
  },
  {
    key: 'terms',
    label: translate(`${STRINGS}.Answers.Terms`),
    value: response.termsResponse,
  },
];

const CallRow = (props) => {
  const {call} = props;
  const result = call.result || 'OPEN';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: '#F9F8F6',
        border: '1px solid #E8E4DE',
      }}
    >
      <Stack direction='row' alignItems='center' sx={{gap: '7px', flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: '13px', fontWeight: 500, color: '#1A1916'}}>
          {translate(`${STRINGS}.Calls.Attempt`, {number: call.attempt})}
        </Typography>
        <Pill
          label={translateOr(`${STRINGS}.Calls.Kind.${call.kind}`, humanizeKind(call.kind))}
          variant='neutral'
        />
        <Pill
          label={translateOr(`${STRINGS}.Calls.Result.${result}`, humanizeKind(result))}
          variant={call.result === 'ANSWERED' ? 'info' : 'neutral'}
        />
      </Stack>
      <Stack spacing={0.25} sx={{mt: 0.5}}>
        <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
          {translate(`${STRINGS}.Calls.BookedFor`, {value: formatDateTime(call.scheduledFor)})}
        </Typography>
        <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
          {
            call.dispatchedAt ?
              translate(`${STRINGS}.Calls.Placed`, {value: formatDateTime(call.dispatchedAt)}) :
              translate(`${STRINGS}.Calls.NotPlaced`)
          }
        </Typography>
        {
          call.finishedAt &&
          <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
            {translate(`${STRINGS}.Calls.Finished`, {value: formatDateTime(call.finishedAt)})}
          </Typography>
        }
        {
          call.note &&
          <Typography sx={{fontSize: '12px', color: '#6B6760', whiteSpace: 'pre-wrap'}}>
            {translate(`${STRINGS}.Calls.Note`, {value: call.note})}
          </Typography>
        }
      </Stack>
    </Box>
  );
};
CallRow.propTypes = {
  call: PropTypes.object.isRequired,
};

const InvitationCard = (props) => {
  const {invitation, caregiverName, onCancel, onShared} = props;
  const {job, workflow, response} = invitation;
  const calls = invitation.calls || [];
  const familyAnswers = invitation.familyAnswers || [];
  const progress = workflow?.steadyState || (invitation.status === 'WAITING' ? 'STARTING' : null);

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', minWidth: 0}}>
          <JobIcon sx={{mr: 1}} />
          <Typography variant='h6' sx={{wordBreak: 'break-word'}}>
            {job?.summary || translate(`${STRINGS}.JobMissing`)}
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' sx={{gap: 1}}>
          {
            job &&
            <Link href={`/career/loom/${encodeURIComponent(job.id)}`} sx={{fontSize: '13px'}}>
              {translate(`${STRINGS}.OpenJob`)}
            </Link>
          }
          {
            invitation.status === 'WAITING' &&
            <Button
              variant='outlined'
              size='small'
              color='error'
              onClick={() => onCancel(invitation)}
              sx={{textTransform: 'none'}}
            >
              {translate('career.jobDetail.Invitations.Cancel')}
            </Button>
          }
          <Chip
            label={translateOr(`career.jobDetail.Invitations.Status.${invitation.status}`, humanizeKind(invitation.status))}
            color={STATUS_COLOURS[invitation.status] || 'default'}
            size='small'
          />
        </Stack>
      </Box>
      <Stack spacing={2} sx={{p: 2}}>
        <Stack direction='row' sx={{gap: '7px', flexWrap: 'wrap'}}>
          <Pill
            label={translate(`${STRINGS}.Sent`, {value: formatDateTime(invitation.createdAt)})}
            variant='neutral'
          />
          {
            invitation.closedAt &&
            <Pill
              label={translate(`${STRINGS}.Closed`, {value: formatDateTime(invitation.closedAt)})}
              variant='neutral'
            />
          }
          {
            progress &&
            <Pill
              label={`${translate(`${STRINGS}.Progress`)}: ${translateOr(`career.jobDetail.Invitations.Progress.${progress}`, humanizeKind(progress))}`}
              variant='info'
            />
          }
          {
            job?.city &&
            <Pill label={job.city} variant='neutral' />
          }
          {
            job?.days?.length > 0 &&
            <Pill
              label={job.days.map((day) => translateOr(`job.ClientJobs.Days.${day}`, humanizeKind(day))).join(', ')}
              variant='neutral'
            />
          }
        </Stack>
        {
          (invitation.declineReason || invitation.requestedDoNotContact) &&
          <Stack spacing={0.25}>
            {
              invitation.declineReason &&
              <Typography variant='body2'>
                {translate('career.jobDetail.Invitations.DeclineReason', {reason: invitation.declineReason})}
              </Typography>
            }
            {
              invitation.requestedDoNotContact &&
              <Typography variant='body2' color='error.main'>
                {translate('career.jobDetail.Invitations.DoNotContact')}
              </Typography>
            }
          </Stack>
        }
        <Stack spacing={1}>
          <SectionTitle>{translate(`${STRINGS}.Calls.Title`)}</SectionTitle>
          {
            calls.length === 0 &&
            <Typography sx={{fontSize: '13px', color: '#6B6760'}}>
              {translate(`${STRINGS}.Calls.Empty`)}
            </Typography>
          }
          {
            calls.map((call) => (
              <CallRow key={call.id} call={call} />
            ))
          }
        </Stack>
        <Stack spacing={1}>
          <SectionTitle>{translate(`${STRINGS}.Answers.Title`)}</SectionTitle>
          {
            !response &&
            <Typography sx={{fontSize: '13px', color: '#6B6760'}}>
              {translate(`${STRINGS}.Answers.Empty`)}
            </Typography>
          }
          {
            response &&
            <Box
              sx={{
                display: 'grid',
                border: '1px solid #E8E4DE',
                borderRadius: '12px',
                overflow: 'hidden',
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {
                responseDetails(response).map((detail) => (
                  <DetailCell
                    key={detail.key}
                    label={detail.label}
                    value={detail.value}
                  />
                ))
              }
            </Box>
          }
        </Stack>
        {
          familyAnswers.length > 0 &&
          <InvitationFamilyAnswers
            invitation={invitation}
            caregiverName={caregiverName}
            onShared={onShared}
          />
        }
      </Stack>
    </Paper>
  );
};
InvitationCard.propTypes = {
  invitation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    closedAt: PropTypes.string,
    declineReason: PropTypes.string,
    requestedDoNotContact: PropTypes.bool,
    job: PropTypes.object,
    workflow: PropTypes.object,
    calls: PropTypes.array,
    response: PropTypes.object,
    familyAnswers: PropTypes.array,
    sharedWithFamilyAt: PropTypes.string,
  }).isRequired,
  caregiverName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onShared: PropTypes.func.isRequired,
};

export default React.memo(InvitationCard);
